import httpClient from './httpClient';

// Types for Interview Settings
export interface InterviewSettings {
  difficulty: 'easy' | 'medium' | 'hard';
  experience_focus: 'varied' | 'recent_deep';
  mode: 'technical' | 'behavioral' | 'professional';
  response_feedback: boolean;
  vibe_gifs_enabled: boolean;
  use_voice_agent?: boolean;
  accent?: 'en-IN' | 'en-US';
  interviewer_gender?: 'female' | 'male';
}

export interface InterviewerPreview {
  name: string;
  gemini_voice: string;
  language_code: string;
}

export interface ValidOptions {
  difficulty: string[];
  experience_focus: string[];
  mode: string[];
  accent?: string[];
  interviewer_gender?: string[];
}

export interface InterviewSettingsData {
  settings: InterviewSettings;
  resume_link: string;
  summary: string;
  valid_options: ValidOptions;
  interviewer_preview?: InterviewerPreview;
}

export interface InterviewSettingsResponse {
  success: boolean;
  data: InterviewSettingsData;
}

export interface UpdateSettingsRequest {
  difficulty?: string;
  experience_focus?: string;
  mode?: string;
  response_feedback?: boolean;
  vibe_gifs_enabled?: boolean;
  use_voice_agent?: boolean;
  accent?: string;
  interviewer_gender?: string;
}

export interface UpdateSettingsResponse {
  success: boolean;
  message?: string;
  data?: InterviewSettings;
}

// API functions
export const settingsApi = {
  /**
   * Get user's interview settings
   * GET /api/user/interview-settings
   */
  async getInterviewSettings(): Promise<InterviewSettingsResponse> {
    const response = await httpClient.get('user/interview-settings');
    return response.data;
  },

  /**
   * Update user's interview settings
   * PUT /api/user/interview-settings
   * @param settings - The settings to update
   */
  async updateInterviewSettings(
    settings: UpdateSettingsRequest
  ): Promise<UpdateSettingsResponse> {
    const response = await httpClient.put('user/interview-settings', settings);
    return response.data;
  },
};

// Error handling helper
export const handleSettingsError = (error: any): string => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.response?.status === 400) {
    return 'Invalid settings. Please check your selections.';
  }

  if (error.response?.status === 401) {
    return 'Authentication failed. Please log in again.';
  }

  if (error.response?.status === 404) {
    return 'Settings not found. Please try again.';
  }

  if (error.response?.status === 500) {
    return 'Server error. Please try again later.';
  }

  if (error.code === 'NETWORK_ERROR' || !error.response) {
    return 'Network error. Please check your connection.';
  }

  return 'An unexpected error occurred. Please try again.';
};

export default settingsApi;



